import { useState } from 'react';
import { AlertCircle, FileText, CheckCircle2, Lock, Flame, AlertTriangle } from 'lucide-react';

import { tryParseDeep } from '../../lib/json-parser';

import { useExecutionData, type ProcessedEvent } from './inspector-context';
import { JsonHighlight } from './json-highlight';

const METHOD_COLORS: Record<string, string> = {
  GET: 'text-[var(--flux-success)] bg-[var(--flux-success)]/10 border-[var(--flux-success)]/20',
  POST: 'text-[var(--flux-fetch)] bg-[var(--flux-fetch)]/10 border-[var(--flux-fetch)]/20',
  PUT: 'text-[var(--flux-warning)] bg-[var(--flux-warning)]/10 border-[var(--flux-warning)]/20',
  PATCH: 'text-[var(--flux-warning)] bg-[var(--flux-warning)]/10 border-[var(--flux-warning)]/20',
  DELETE: 'text-[var(--flux-error)] bg-[var(--flux-error)]/10 border-[var(--flux-error)]/20',
  HEAD: 'text-muted-foreground bg-muted/40 border-border/50',
};

interface NetworkPanelProps {
  events: ProcessedEvent[];
  startAt: number;
  onSelect: (idx: number) => void;
  initialSelectedIdx?: number | null | undefined;
}

function isProtected(payload: unknown): boolean {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    (payload as Record<string, unknown>)['__protected'] === true
  );
}

function parseJsonPayload(raw: unknown): Record<string, unknown> | null {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return null;
    }
  }
  return raw as Record<string, unknown>;
}

function statusColor(
  status: number,
):
  | 'text-[var(--flux-success)]'
  | 'text-[var(--flux-warning)]'
  | 'text-[var(--flux-error)]'
  | 'text-[var(--flux-fetch)]' {
  if (status >= 200 && status < 300) return 'text-[var(--flux-success)]';
  if (status >= 300 && status < 400) return 'text-[var(--flux-fetch)]';
  if (status >= 400 && status < 500) return 'text-[var(--flux-warning)]';
  return 'text-[var(--flux-error)]';
}

function getHostName(url: string): string {
  if (!url || url.includes('[encrypted') || !url.startsWith('http')) return 'Unknown Service';
  try {
    const u = new URL(url);
    if (u.hostname === 'localhost' || u.hostname === '127.0.0.1') return 'Local API';
    return u.hostname;
  } catch {
    return 'Unknown Service';
  }
}

function getPathname(url: string): string {
  if (!url || url.includes('[encrypted') || !url.startsWith('http')) return url;
  try {
    const u = new URL(url);
    return u.pathname + u.search;
  } catch {
    return url;
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function getImpact(duration: number, status: number, _totalDuration: number) {
  if (status >= 500)
    return {
      icon: <Flame className="w-3 h-3 text-[var(--flux-error)]" />,
      text: 'Broke execution',
      listHelp: 'Broke execution',
      insight: '🔥 This request failed with a server error, breaking the execution.',
      reason: 'Upstream service returned an error status.',
      class: 'text-[var(--flux-error)] bg-[var(--flux-error)]/10 border-[var(--flux-error)]/20',
    };
  if (duration > 600)
    return {
      icon: <Flame className="w-3 h-3 text-[var(--flux-error)]" />,
      text: 'Bottleneck',
      listHelp: `Delayed execution by ${duration}ms`,
      insight: `🔥 This request added a massive ${duration}ms delay and is on the critical path.`,
      reason: 'Execution waited for this slow response before continuing.',
      class: 'text-[var(--flux-error)] bg-[var(--flux-error)]/10 border-[var(--flux-error)]/20',
    };
  if (duration > 300)
    return {
      icon: <AlertTriangle className="w-3 h-3 text-[var(--flux-warning)]" />,
      text: 'Blocking execution',
      listHelp: `Delayed execution by ${duration}ms`,
      insight: `⚠ This request added ${duration}ms delay and is on the critical path.`,
      reason: 'Slower than expected response bounded the execution.',
      class:
        'text-[var(--flux-warning)] bg-[var(--flux-warning)]/10 border-[var(--flux-warning)]/20',
    };
  return {
    icon: <CheckCircle2 className="w-3 h-3 text-muted-foreground/70" />,
    text: 'Not critical',
    listHelp: 'No impact on execution',
    insight: '✔ This request completed successfully and did not affect execution time.',
    reason: 'Executed cleanly within budget.',
    class: 'text-muted-foreground',
  };
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

const SENSITIVE_HEADER_PATTERN = /authorization|cookie|set-cookie|x-api-key|api-key|token|secret/i;

function maskHeaders(headers: unknown, reveal: boolean): Record<string, unknown> {
  if (headers == null || typeof headers !== 'object') return {};
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(headers as Record<string, unknown>)) {
    if (!reveal && SENSITIVE_HEADER_PATTERN.test(key)) {
      // Preserve prefix like "Bearer " but mask the credential
      if (typeof value === 'string' && value.includes(' ')) {
        const parts = value.split(' ');
        result[key] = parts[0] + ' ********';
      } else {
        result[key] = '********';
      }
    } else {
      result[key] = value;
    }
  }
  return result;
}

export function NetworkPanel({
  onSelect,
  initialSelectedIdx = null,
}: Omit<NetworkPanelProps, 'events' | 'startAt'>): import('react/jsx-runtime').JSX.Element {
  const { networkCalls: events, startAt, durationMs: totalDuration } = useExecutionData();
  const [selectedIdx, setSelectedIdx] = useState<number | null>(initialSelectedIdx);

  const fetchPairs = events as import('./inspector-context').SpanEvent[];

  return (
    <div
      className={`flex ${selectedIdx !== null ? 'flex-col md:flex-row' : 'flex-col'} h-full bg-background`}
    >
      <div
        className={`flex-1 overflow-auto border-r border-border/40 ${selectedIdx !== null ? 'md:max-w-[45%]' : ''}`}
      >
        {fetchPairs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
            <div className="w-12 h-12 rounded-full border border-dashed border-border/60 flex items-center justify-center mb-3">
              <FileText className="w-5 h-5 opacity-50" />
            </div>
            <p className="text-sm font-medium">No network requests</p>
            <p className="text-xs opacity-70 mt-1">
              This execution didn&apos;t make any fetch calls.
            </p>
          </div>
        ) : (
          <div className="p-2 space-y-1">
            {fetchPairs.map((pair, i): import('react/jsx-runtime').JSX.Element => {
              const reqPayload = pair.payload as Record<string, unknown> | null;
              const resRaw = pair.responsePayload;
              const resPayload = parseJsonPayload(resRaw);

              const isArrayLike = reqPayload != null && '0' in reqPayload;
              const reqOptions = reqPayload?.['options'] as Record<string, unknown> | undefined;
              const reqInit = reqPayload?.['init'] as Record<string, unknown> | undefined;
              const rawUrl = isArrayLike
                ? ((reqPayload['0'] as string | undefined) ?? '')
                : ((reqPayload?.['input'] as string | undefined) ??
                  (reqPayload?.['url'] as string | undefined) ??
                  (reqOptions?.['url'] as string | undefined) ??
                  '');

              const initObj = isArrayLike
                ? (reqPayload['1'] as Record<string, unknown> | undefined)
                : (reqInit ?? reqOptions);

              const url = rawUrl;
              const pathname = getPathname(url);
              const host = getHostName(url);

              const method = ((): string => {
                const m = initObj?.['method'] ?? reqPayload?.['method'];
                return typeof m === 'string' ? m.toUpperCase() : 'GET';
              })();

              const status = Number(resPayload?.['status'] ?? 0);
              const duration = pair.durationMs ?? 0;

              const impact = getImpact(duration, status, totalDuration);
              const isSelected = selectedIdx === i;

              // Estimate size
              let sizeStr = '-';
              if (resPayload?.['headers'] != null && typeof resPayload['headers'] === 'object') {
                const headers = resPayload['headers'] as Record<string, string>;
                const cl = Object.keys(headers).find(
                  (k): boolean => k.toLowerCase() === 'content-length',
                );
                if (cl && headers[cl]) sizeStr = formatBytes(parseInt(headers[cl], 10));
              }

              return (
                <div
                  key={pair.id}
                  onClick={(): void => {
                    setSelectedIdx(i);
                    const targetIdx = events.findIndex((e): boolean => e.id === pair.id);
                    if (targetIdx !== -1) onSelect(targetIdx);
                  }}
                  className={`cursor-pointer border rounded-md p-3 text-sm transition-colors ${
                    isSelected
                      ? 'bg-accent/50 border-accent-foreground/20'
                      : 'bg-card border-border/40 hover:border-border/80'
                  }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2 overflow-hidden w-full pr-4">
                      <span
                        className={`inline-block font-mono text-[10px] font-bold rounded px-1.5 py-0.5 border ${
                          METHOD_COLORS[method] ?? 'text-muted-foreground bg-muted/40 border-border'
                        }`}
                      >
                        {method}
                      </span>
                      <span className="font-mono text-xs truncate" title={url}>
                        {pathname}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0 font-mono text-xs">
                      <span
                        className={`font-medium ${status ? statusColor(status) : 'text-muted-foreground'}`}
                      >
                        {status || '-'}
                      </span>
                      <span
                        className={
                          duration > 300
                            ? duration > 600
                              ? 'text-[var(--flux-error)] font-medium'
                              : 'text-[var(--flux-warning)] font-medium'
                            : 'text-muted-foreground'
                        }
                      >
                        {duration > 0 ? `${duration}ms` : '-'}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-1.5 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1.5">
                      <span className="opacity-60 text-[10px] uppercase font-semibold">
                        Service:
                      </span>
                      <span className="font-medium text-foreground/80">{host}</span>
                    </div>

                    <div className="flex items-center gap-1.5">
                      <span className="opacity-60 text-[10px] uppercase font-semibold">Size:</span>
                      <span>{sizeStr}</span>
                    </div>

                    <div
                      className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full border ${impact.class}`}
                    >
                      {impact.icon}
                      <span className="font-medium text-[10px] uppercase tracking-wider">
                        {impact.listHelp ?? impact.text}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {selectedIdx !== null && fetchPairs[selectedIdx] != null && (
        <div className="flex-1 bg-background flex flex-col h-full overflow-hidden border-l border-border/40">
          <NetworkDetail
            pair={fetchPairs[selectedIdx]}
            startAt={startAt}
            totalDuration={totalDuration}
            onClose={(): void => setSelectedIdx(null)}
          />
        </div>
      )}
    </div>
  );
}

function NetworkDetail({
  pair,
  startAt,
  totalDuration,
  onClose,
}: {
  pair: import('./inspector-context').SpanEvent;
  startAt: number;
  totalDuration: number;
  onClose: () => void;
}): import('react/jsx-runtime').JSX.Element {
  const [tab, setTab] = useState<'overview' | 'request' | 'response' | 'timing' | 'trace'>(
    'overview',
  );
  const [revealSecrets, setRevealSecrets] = useState(false);

  const reqPayload = pair.payload as Record<string, unknown> | null;
  const resRaw = pair.responsePayload;
  const resPayload = parseJsonPayload(resRaw);

  const reqProtected = isProtected(reqPayload);
  const resProtected = isProtected(resRaw);

  const isArrayLike = reqPayload != null && '0' in reqPayload;
  const rp = reqPayload as Record<string, unknown>;
  const rawUrl = isArrayLike
    ? typeof reqPayload['0'] === 'string'
      ? reqPayload['0']
      : ''
    : ((): string => {
        const v =
          rp?.['input'] ?? rp?.['url'] ?? (rp?.['options'] as Record<string, unknown>)?.['url'];
        return typeof v === 'string' ? v : '';
      })();

  const initObj = isArrayLike
    ? (reqPayload['1'] as Record<string, unknown> | undefined)
    : ((rp?.['init'] ?? rp?.['options']) as Record<string, unknown> | undefined);

  const url = rawUrl;
  const method = (
    (initObj?.['method'] as string | undefined) ??
    (rp?.['method'] as string | undefined) ??
    'GET'
  ).toUpperCase();
  const status = Number(resPayload?.['status'] ?? 0);
  const duration = pair.durationMs ?? 0;

  const host = getHostName(url);
  const impact = getImpact(duration, status, totalDuration);

  let sizeStr = 'Unknown';
  if (resPayload?.['headers'] != null && typeof resPayload['headers'] === 'object') {
    const headers = resPayload['headers'] as Record<string, string>;
    const cl = Object.keys(headers).find((k): boolean => k.toLowerCase() === 'content-length');
    if (cl && headers[cl]) sizeStr = formatBytes(parseInt(headers[cl], 10));
  }

  const tabs = ['overview', 'request', 'response', 'timing', 'trace'] as const;

  return (
    <div className="flex flex-col h-full isolate">
      <div className="flex items-center justify-between px-4 py-2 border-b border-border/40 shrink-0 bg-muted/20">
        <div className="flex items-center gap-3 overflow-hidden">
          <span
            className={`inline-block font-mono text-[10px] font-bold rounded px-1.5 py-0.5 border shrink-0 ${
              METHOD_COLORS[method] ?? 'text-muted-foreground bg-muted/40 border-border'
            }`}
          >
            {method}
          </span>
          <span className="font-mono text-sm truncate font-medium" title={url}>
            {getPathname(url)}
          </span>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-muted rounded text-muted-foreground">
          <svg
            width="15"
            height="15"
            viewBox="0 0 15 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11.7816 4.03157C12.0062 3.80702 12.0062 3.44295 11.7816 3.2184C11.5571 2.99385 11.193 2.99385 10.9685 3.2184L7.50005 6.68682L4.03164 3.2184C3.80708 2.99385 3.44301 2.99385 3.21846 3.2184C2.99391 3.44295 2.99391 3.80702 3.21846 4.03157L6.68688 7.49999L3.21846 10.9684C2.99391 11.193 2.99391 11.557 3.21846 11.7816C3.44301 12.0061 3.80708 12.0061 4.03164 11.7816L7.50005 8.31316L10.9685 11.7816C11.193 12.0061 11.5571 12.0061 11.7816 11.7816C12.0062 11.557 12.0062 11.193 11.7816 10.9684L8.31322 7.49999L11.7816 4.03157Z"
              fill="currentColor"
              fillRule="evenodd"
              clipRule="evenodd"
            ></path>
          </svg>
        </button>
      </div>

      <div className="flex gap-1 px-3 py-2 border-b border-border/40 shrink-0 overflow-x-auto scroller-hide bg-muted/10">
        {tabs.map((t): import('react/jsx-runtime').JSX.Element => (
          <button
            key={t}
            onClick={(): void => setTab(t)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all capitalize shrink-0 ${
              tab === t
                ? 'bg-foreground text-background shadow-sm'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-auto p-4">
        {tab === 'overview' && (
          <div className="space-y-6">
            <div className="space-y-3">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b pb-2">
                Request Summary
              </h3>
              <div className="grid grid-cols-[100px_1fr] gap-y-2 text-sm">
                <div className="text-muted-foreground">URL:</div>
                <div className="font-mono break-all text-xs">{url}</div>

                <div className="text-muted-foreground">Status:</div>
                <div
                  className={`font-mono font-medium ${status ? statusColor(status) : 'text-muted-foreground'}`}
                >
                  {status
                    ? `${status} ${(resPayload?.['statusText'] as string | undefined) ?? ''}`
                    : resProtected
                      ? 'Locked'
                      : 'Unknown'}
                </div>

                <div className="text-muted-foreground">Duration:</div>
                <div className="font-mono">{duration > 0 ? `${duration}ms` : '–'}</div>

                <div className="text-muted-foreground">Service:</div>
                <div className="font-medium">{host}</div>

                <div className="text-muted-foreground">Size:</div>
                <div>{sizeStr}</div>

                <div className="text-muted-foreground">Retries:</div>
                <div className="font-mono" data-testid="retries-count">
                  0
                </div>

                <div className="text-muted-foreground">Impact:</div>
                <div className="flex flex-col gap-0.5">
                  <div className="flex items-center gap-1.5">
                    {impact.icon} <span className="font-medium">{impact.text}</span>
                  </div>
                  {impact.insight && (
                    <div className="text-xs text-muted-foreground">{impact.insight}</div>
                  )}
                  {impact.reason && (
                    <div className="text-xs text-muted-foreground">{impact.reason}</div>
                  )}
                </div>
              </div>
            </div>

            {status >= 400 && (
              <div className="rounded-md border border-[var(--flux-error)]/30 bg-[var(--flux-error)]/5 p-3">
                <div className="flex items-start gap-3">
                  <Flame className="w-5 h-5 text-[var(--flux-error)] shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-semibold text-[var(--flux-error)]">
                      Failed Request: {status}
                    </h4>
                    <p className="text-xs text-foreground/80 mt-1">
                      This request resulted in an error status code. Check the Response tab for more
                      details about the failure.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-3">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b pb-2">
                Sensitive Data
              </h3>
              {reqProtected || resProtected ? (
                <div className="flex items-center gap-2 p-3 bg-muted/40 rounded-md border border-border/50">
                  <Lock className="w-4 h-4 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">
                    Encrypted segments detected. Raw encrypted payloads are shown in the
                    request/response tabs.
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2 p-3 bg-muted/10 rounded-md border border-border/30">
                  <CheckCircle2 className="w-4 h-4 text-[var(--flux-success)]" />
                  <span className="text-xs text-muted-foreground">
                    No encrypted segments in this request.
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === 'request' && (
          <div className="space-y-6">
            <div className="space-y-2">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Headers
              </h3>
              <div className="rounded-md border bg-muted/10 overflow-hidden">
                {reqProtected && (
                  <div className="p-2 text-xs text-muted-foreground border-b border-border/40 flex items-center gap-2">
                    <Lock className="w-4 h-4" /> Request is encrypted
                  </div>
                )}
                <JsonHighlight value={maskHeaders(initObj?.['headers'], revealSecrets)} />
                <button
                  onClick={(): void => setRevealSecrets((r): boolean => !r)}
                  className="text-xs text-muted-foreground px-3 py-1.5 hover:text-foreground border-t border-border/40 w-full text-left"
                >
                  {revealSecrets ? 'Hide sensitive values' : 'Reveal locally'}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Body
              </h3>
              <div className="rounded-md border bg-muted/10 overflow-hidden">
                {reqProtected && (
                  <div className="p-2 text-xs text-muted-foreground border-b border-border/40 flex items-center gap-2">
                    <Lock className="w-4 h-4" /> Request body is encrypted
                  </div>
                )}
                <JsonHighlight value={tryParseDeep(initObj?.['body'] ?? null).parsed} />
              </div>
            </div>
          </div>
        )}

        {tab === 'response' && (
          <div className="space-y-6">
            <div className="space-y-2">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Headers
              </h3>
              <div className="rounded-md border bg-muted/10 overflow-hidden">
                {resProtected && (
                  <div className="p-2 text-xs text-muted-foreground border-b border-border/40 flex items-center gap-2">
                    <Lock className="w-4 h-4" /> Response is encrypted
                  </div>
                )}
                <JsonHighlight value={resPayload?.['headers'] ?? {}} />
              </div>
            </div>
            <div className="space-y-2">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Body / Data
              </h3>
              <div className="rounded-md border bg-muted/10 overflow-hidden">
                {resProtected && (
                  <div className="p-2 text-xs text-muted-foreground border-b border-border/40 flex items-center gap-2">
                    <Lock className="w-4 h-4" /> Response payload is encrypted
                  </div>
                )}
                <JsonHighlight value={tryParseDeep(resPayload?.['body'] ?? resRaw).parsed} />
              </div>
            </div>
          </div>
        )}

        {tab === 'timing' && (
          <div className="space-y-6">
            <div className="space-y-3">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b pb-2">
                Timing Breakdown
              </h3>
              <div className="font-mono text-sm space-y-1 bg-muted/10 border rounded-md p-3">
                <div className="flex justify-between py-1">
                  <span className="text-muted-foreground">Duration:</span>
                  <span>{duration}ms</span>
                </div>
                <div className="flex justify-between py-1 border-t border-dashed border-border/50">
                  <span className="text-muted-foreground">DNS lookup:</span>
                  <span className="text-muted-foreground/70">--</span>
                </div>
                <div className="flex justify-between py-1">
                  <span className="text-muted-foreground">Connection:</span>
                  <span className="text-muted-foreground/70">--</span>
                </div>
                <div className="flex justify-between py-1">
                  <span className="text-muted-foreground">TTFB:</span>
                  <span className="text-muted-foreground/70">--</span>
                </div>
                <div className="flex justify-between py-1">
                  <span className="text-muted-foreground">Download:</span>
                  <span className="text-muted-foreground/70">--</span>
                </div>
              </div>
              <p className="text-[10px] text-muted-foreground/60 text-center uppercase tracking-widest pt-2">
                Detailed phases available in full trace mode
              </p>
            </div>

            <div className="space-y-3">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b pb-2">
                Waterfall
              </h3>
              <div className="mt-4">
                <div className="w-full bg-muted/30 h-6 border rounded-sm relative overflow-hidden flex items-center">
                  <div
                    className={`absolute h-full ${impact.class.includes('error') ? 'bg-[var(--flux-error)]' : impact.class.includes('warning') ? 'bg-[var(--flux-warning)]' : 'bg-primary'} opacity-70`}
                    style={{
                      left: `${Math.max(0, ((pair.at - startAt) / totalDuration) * 100)}%`,
                      width: `${Math.max(1, (duration / totalDuration) * 100)}%`,
                    }}
                  />
                </div>
                <div className="flex justify-between text-[10px] text-muted-foreground mt-1.5 font-mono">
                  <span>0ms</span>
                  <span>{totalDuration}ms</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === 'trace' && (
          <div className="space-y-6">
            <div className="space-y-3">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b pb-2">
                Execution Context
              </h3>

              <div className="grid grid-cols-[100px_1fr] gap-y-3 text-sm">
                <div className="text-muted-foreground">Span ID:</div>
                <div className="font-mono text-xs">{pair.id}</div>

                <div className="text-muted-foreground">Started at:</div>
                <div className="font-mono text-xs">+{Math.round(pair.at - startAt)}ms</div>

                <div className="text-muted-foreground">Type:</div>
                <div className="font-mono text-xs text-[var(--flux-fetch)]">fetch</div>
              </div>
            </div>

            <div className="bg-muted/10 border rounded-md p-4 flex flex-col items-center justify-center text-center mt-8">
              <AlertCircle className="w-8 h-8 text-muted-foreground/40 mb-2" />
              <p className="text-sm font-medium">Coming soon</p>
              <p className="text-xs text-muted-foreground mt-1 max-w-[250px]">
                Full causal linkage and &quot;Triggered by&quot; graph will be available in detailed
                tracing mode.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
